import React from "react";
import { FiX } from "react-icons/fi";
import "../styles/MemberDetailsModal.css";

/**
 * Displays all member fields as collected in the Add Member form.
 * fullUser: optional object from getSingleUser (id, firstName, lastName, email, contact, DOB, address, gender, roles).
 * member: table row (id, name, title, email, contact, dbId).
 */
const MemberDetailsModal = ({ member, fullUser = null, onClose }) => {
  if (!member) return null;

  const u = fullUser || {};
  const display = (v) => (v != null && v !== "" ? String(v) : "—");

  // Superannuation section is shown for Admin, Manager, Cleaner (not Contractor)
  const showSuperannuation =
    member.title === "Admin" ||
    member.title === "Manager" ||
    member.title === "Cleaner";

  const isContractor = member.title === "Contractor";

  return (
    <div className="member-details-overlay" onClick={onClose}>
      <div className="member-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="member-details-header">
          <h2>Member Details</h2>
          <button type="button" className="member-details-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <div className="member-details-body">
          {/* Personal Info */}
          <section className="member-details-section">
            <h3>Personal Info</h3>
            <div className="member-details-grid">
              <div className="member-details-item">
                <span className="label">Employee ID</span>
                <span className="value">{display(member.id)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Name</span>
                <span className="value">{display(member.name)}</span>
              </div>
              {isContractor && (
                <div className="member-details-item">
                  <span className="label">Business Name</span>
                  <span className="value">{display(u.businessName)}</span>
                </div>
              )}
              <div className="member-details-item">
                <span className="label">Title</span>
                <span className="value">{display(member.title)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">DOB</span>
                <span className="value">{display(u.DOB)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Email</span>
                <span className="value">{display(member.email || u.email)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Contact Number</span>
                <span className="value">{display(member.contact || u.contact)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Address</span>
                <span className="value">{display(u.address)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Gender</span>
                <span className="value">{display(u.gender)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Tax Residency</span>
                <span className="value">{display(u.taxResidency)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Emergency Contact (Name)</span>
                <span className="value">{display(u.emergencyContactName)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Emergency Contact (Number)</span>
                <span className="value">{display(u.emergencyContactNumber)}</span>
              </div>
            </div>
          </section>

          {/* Financial Info */}
          <section className="member-details-section">
            <h3>Financial Info</h3>
            <div className="member-details-grid">
              <div className="member-details-item">
                <span className="label">BSB</span>
                <span className="value">{display(u.bsb)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Account Number</span>
                <span className="value">{display(u.accountNumber)}</span>
              </div>
              {isContractor && (
                <div className="member-details-item">
                  <span className="label">Account Name</span>
                  <span className="value">{display(u.accountName)}</span>
                </div>
              )}
              {!isContractor && (
                <div className="member-details-item">
                  <span className="label">Tax File Number</span>
                  <span className="value">{display(u.taxFileNumber)}</span>
                </div>
              )}
              {isContractor && (
                <div className="member-details-item">
                  <span className="label">ABN Number</span>
                  <span className="value">{display(u.abnNumber)}</span>
                </div>
              )}
              {!isContractor && (
                <div className="member-details-item">
                  <span className="label">Claim tax-free threshold</span>
                  <span className="value">{display(u.claimTaxFreeThreshold)}</span>
                </div>
              )}
            </div>
          </section>

          {/* Superannuation Info - shown for Admin, Manager, Cleaner */}
          {showSuperannuation && (
            <section className="member-details-section">
              <h3>Superannuation Info</h3>
              <div className="member-details-grid">
                <div className="member-details-item">
                  <span className="label">Fund Name</span>
                  <span className="value">{display(u.fundName)}</span>
                </div>
                <div className="member-details-item">
                  <span className="label">USI</span>
                  <span className="value">{display(u.spin)}</span>
                </div>
                <div className="member-details-item">
                  <span className="label">Fund ABN</span>
                  <span className="value">{display(u.fundABN)}</span>
                </div>
                <div className="member-details-item">
                  <span className="label">Fund Telephone Number</span>
                  <span className="value">{display(u.fundTelephoneNumber)}</span>
                </div>
                <div className="member-details-item">
                  <span className="label">Member Number</span>
                  <span className="value">{display(u.memberNumber)}</span>
                </div>
              </div>
            </section>
          )}

          {/* Professional Info */}
          <section className="member-details-section">
            <h3>Professional Info</h3>
            <div className="member-details-grid">
              {!isContractor && (
                <div className="member-details-item">
                  <span className="label">Level</span>
                  <span className="value">{display(u.jobTitle)}</span>
                </div>
              )}
              {isContractor && (
                <div className="member-details-item">
                  <span className="label">Pay Type</span>
                  <span className="value">{display(u.payType) === "pay-per-hour" ? "Pay per hour" : display(u.payType) === "pay-per-project" ? "Pay per project" : display(u.payType)}</span>
                </div>
              )}
              <div className="member-details-item">
                <span className="label">Per Hour Rate</span>
                <span className="value">{display(u.perHourRate)}</span>
              </div>
              {!isContractor && (
                <>
                  <div className="member-details-item">
                    <span className="label">Super Annuation (%)</span>
                    <span className="value">{display(u.superAnnuation)}</span>
                  </div>
                  <div className="member-details-item">
                    <span className="label">Salary Term</span>
                    <span className="value">{display(u.salaryTerm)}</span>
                  </div>
                  <div className="member-details-item">
                    <span className="label">Type of Work</span>
                    <span className="value">{display(u.typeOfWork)}</span>
                  </div>
                </>
              )}
              <div className="member-details-item">
                <span className="label">Payday (Day / Time)</span>
                <span className="value">
                  {display(u.paydayDay)} {display(u.paydayTime)}
                </span>
              </div>
              <div className="member-details-item">
                <span className="label">Joining Date</span>
                <span className="value">{display(u.joiningDate)}</span>
              </div>
            </div>
          </section>

          {/* User Settings */}
          <section className="member-details-section">
            <h3>User Settings</h3>
            <div className="member-details-grid">
              <div className="member-details-item">
                <span className="label">Email</span>
                <span className="value">{display(member.email || u.email)}</span>
              </div>
              <div className="member-details-item">
                <span className="label">Role</span>
                <span className="value">
                  {display(member.title || (u.roles && u.roles[0]))}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MemberDetailsModal;
